import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");

    if (!(details instanceof HTMLDetailsElement))
      throw new Error("Details element not found");

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");

    if (!(summary instanceof HTMLElement))
      throw new Error("Summary element not found");

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    return this.details.querySelector('[class*="__content"]');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  #controller = new AbortController();
  #isClosing = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, {
      signal,
    });

    const target = this.closest(".shopify-section, dialog");
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if (
      (isMobile && this.#disableOnMobile) ||
      (isDesktop && this.#disableOnDesktop)
    ) {
      event.preventDefault();
      return;
    }

    // Handle animated closing - prevent default and animate before closing
    // Only intercept if already open (closing action) and not already in closing animation
    if (this.details.hasAttribute("open") && !this.#isClosing) {
      event.preventDefault();
      this.#animateClose();
    }

    // For opening, let the browser handle it normally
  };

  /**
   * Animates the accordion closing before removing the open attribute.
   * This prevents the jerky closing behavior caused by the browser
   * immediately removing content when the details element closes.
   */
  #animateClose() {
    this.#isClosing = true;

    // Add closing class to trigger the CSS transition
    this.details.classList.add("closing");

    // Wait for the transition to complete before actually closing
    const content = this.content;
    if (content) {
      const handleTransitionEnd = (e) => {
        // Only respond to transitions on the content element itself
        if (e.target !== content) return;

        content.removeEventListener("transitionend", handleTransitionEnd);
        this.details.classList.remove("closing");
        this.details.open = false;
        this.#isClosing = false;
      };

      content.addEventListener("transitionend", handleTransitionEnd);

      // Fallback timeout in case transitionend doesn't fire
      setTimeout(() => {
        if (this.#isClosing) {
          this.details.classList.remove("closing");
          this.details.open = false;
          this.#isClosing = false;
        }
      }, 550); // Slightly longer than the 500ms transition
    } else {
      // No content element found, just close immediately
      this.details.classList.remove("closing");
      this.details.open = false;
      this.#isClosing = false;
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === "Escape" && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  /**
   * Handles variant update event to re-render the accordion content.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;
    const newAccordion = source.querySelector("accordion-custom");

    if (!newAccordion) return;

    this.replaceWith(newAccordion);
  };
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
